import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

export function AuthForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: Location })?.from?.pathname || '/app/profile/feed';
  const { user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [otherType, setOtherType] = useState('');

  const artistTypes = ['Sänger/in', 'Produzent/in', 'Instrumentalist/in', 'Songwriter/in', 'DJ', 'other'];
  
  useEffect(() => {
    // If user is already logged in, redirect to their profile
    if (user) {
      navigate('/app/profile/feed', { replace: true });
    }
  }, [user, navigate]);

  if (user) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Check if input is email or username
        const isEmail = emailOrUsername.includes('@');
        
        if (isEmail) {
          // Login directly with email
          const { error: signInError, data } = await supabase.auth.signInWithPassword({
            email: emailOrUsername,
            password,
          });

          if (signInError) {
            if (signInError.message.includes('Invalid login credentials')) {
              throw new Error('Email oder Passwort ist falsch');
            }
            throw signInError;
          }

          if (!data.user) {
            throw new Error('Anmeldung fehlgeschlagen');
          }
        } else {
          // Login with username
          const { data: profile, error: profileError } = await supabase
  .from('profiles')
  .select('email')
  .eq('username', emailOrUsername)
  .single();
          if (profileError) {
            console.log(profileError.message);
            console.log("Email/Username Eingabe:", emailOrUsername);
          }

          console.log("Profile: " + profile)

          if (profileError) {
            if (profileError.code === 'PGRST116') {
              throw new Error(`Der Benutzername "${emailOrUsername}" wurde nicht gefunden`);
            }
            throw profileError;
          }

          if (!profile.email) {
            throw new Error('Keine Email-Adresse für diesen Benutzer gefunden');
          }

          // Login with the associated email
          const { error: signInError, data } = await supabase.auth.signInWithPassword({
            email: profile.email,
            password,
          });

          if (signInError) {
            if (signInError.message.includes('Invalid login credentials')) {
              throw new Error('Passwort ist falsch');
            }
            throw signInError;
          }

          if (!data.user) {
            throw new Error('Anmeldung fehlgeschlagen');
          }
        }

        navigate('/app/profile/feed', { replace: true });
      } else {
        // Registration
        const { error: signUpError, data } = await supabase.auth.signUp({
          email,
          password,
        });

        if (signUpError) throw signUpError;

        if (!data.user) {
          throw new Error('Registrierung fehlgeschlagen');
        }

        const artistTypes = selectedTypes
          .filter(type => type !== 'other')
          .concat(selectedTypes.includes('other') ? [otherType] : []);

        const { error: profileError } = await supabase
          .from('profiles')
          .insert([
            {
              id: data.user.id,
              username: username.toLowerCase(), // Store username in lowercase
              email,
              artist_types: artistTypes,
              created_at: new Date().toISOString(),
            },
          ]);

        if (profileError) {
          if (profileError.code === '23505') { // Unique violation
            throw new Error('Dieser Benutzername ist bereits vergeben');
          }
          throw profileError;
        }

        navigate('/app/profile/feed', { replace: true });
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError(error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-background-secondary p-8 rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold mb-6 text-center">
          {isLogin ? 'Anmelden' : 'Registrieren'}
        </h1>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded text-red-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {isLogin ? (
            <div>
              <label htmlFor="emailOrUsername" className="block text-sm font-medium mb-1">
                Email oder Benutzername
              </label>
              <input
                id="emailOrUsername"
                type="text"
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value.trim())}
                className="w-full p-2 rounded bg-background-tertiary border border-divider focus:outline-none focus:border-accent-primary"
                required
              />
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                  className="w-full p-2 rounded bg-background-tertiary border border-divider focus:outline-none focus:border-accent-primary"
                  required
                />
              </div>
              <div>
                <label htmlFor="username" className="block text-sm font-medium mb-1">
                  Benutzername
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.trim())}
                  className="w-full p-2 rounded bg-background-tertiary border border-divider focus:outline-none focus:border-accent-primary"
                  required
                />
              </div>
            </>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              Passwort
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 rounded bg-background-tertiary border border-divider focus:outline-none focus:border-accent-primary"
              required
            />
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Künstlertyp (Mehrfachauswahl möglich)
              </label>
              <div className="space-y-2">
                {artistTypes.map((type) => (
                  <label key={type} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTypes([...selectedTypes, type]);
                        } else {
                          setSelectedTypes(selectedTypes.filter(t => t !== type));
                        }
                      }}
                      className="mr-2"
                    />
                    {type === 'other' ? 'Anderes' : type}
                  </label>
                ))}
              </div>

              {selectedTypes.includes('other') && (
                <div className="mt-2">
                  <label htmlFor="otherType" className="block text-sm font-medium mb-1">
                    Anderer Künstlertyp
                  </label>
                  <input
                    id="otherType"
                    type="text"
                    value={otherType}
                    onChange={(e) => setOtherType(e.target.value)}
                    className="w-full p-2 rounded bg-background-tertiary border border-divider focus:outline-none focus:border-accent-primary"
                    required
                  />
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-accent-primary hover:bg-accent-hover rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Laden...' : isLogin ? 'Anmelden' : 'Registrieren'}
          </button>
        </form>

        <button
          onClick={() => {
            setIsLogin(!isLogin);
            setError('');
            setEmailOrUsername('');
            setEmail('');
            setUsername('');
            setPassword('');
            setSelectedTypes([]);
            setOtherType('');
          }}
          className="w-full mt-4 text-text-secondary hover:text-text-primary"
        >
          {isLogin ? 'Noch kein Konto? Registrieren' : 'Bereits registriert? Anmelden'}
        </button>
      </div>
    </div>
  );
}